import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Chip,
  Alert,
  LinearProgress,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useParams, useLocation } from 'react-router-dom';
import DataTable from '../components/DataTable';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';
import TimesheetCalendar from '../components/TimesheetCalendar';
import TimesheetTableView from '../components/TimesheetTableView';

interface TimesheetEntry {
  id: string;
  organizationId: string;
  userId: string;
  taskId: string;
  date: string;
  startTime?: string;
  stopTime?: string;
  hours?: number;
  description?: string;
  isLocked: boolean;
  approvalStatus: string;
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  approvalComments?: string;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  user?: {
    id: string;
    email: string;
    displayName?: string;
  };
  task?: {
    id: string;
    name: string;
    taskNumber: string;
    isActive: boolean;
    account?: {
      id: string;
      name: string;
      company?: {
        id: string;
        name: string;
      };
    };
  };
}

interface TimesheetFormData {
  organizationId: string;
  companyId: string;
  accountId: string;
  taskId: string;
  // ❌ REMOVED: date field
  startTime: string;
  stopTime: string;
  hours: string;
  description: string;
  approvalStatus: string;
}

interface Organization {
  id: string;
  name: string;
}

interface Company {
  id: string;
  name: string;
  organizationId: string;
}

interface Account {
  id: string;
  name: string;
  companyId: string;
  organizationId: string;
}

interface Task {
  id: string;
  name: string;
  accountId: string;
  organizationId: string;
  account?: {
    id: string;
    name: string;
    company?: {
      id: string;
      name: string;
    };
  };
}

interface TimesheetsProps {
  selectedOrganizationId?: string;
  view?: 'calendar' | 'table';
}

export default function Timesheets({ selectedOrganizationId, view }: TimesheetsProps) {
  const { organizationId: urlOrganizationId } = useParams<{ organizationId: string }>();
  const location = useLocation();
  
  // Determine view from route if not provided as prop
  const currentView = view || (location.pathname.includes('/table') ? 'table' : 'calendar');
  
  // All hooks must be called before any conditional returns
  const [timesheetEntries, setTimesheetEntries] = useState<TimesheetEntry[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const { formatDateOnly, formatTimeOnly, formatDateTime, toDateTimeLocal, fromDateTimeLocal } = useDateFormat();
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimesheetEntry | null>(null);
  const [formData, setFormData] = useState<TimesheetFormData>({
    organizationId: '',
    companyId: '',
    accountId: '',
    taskId: '',
    // ❌ REMOVED: date field
    startTime: '',
    stopTime: '',
    hours: '',
    description: '',
    approvalStatus: 'draft'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Determine if this is the platform admin view (not organization-specific)
  const isPlatformAdminView = !location.pathname.includes('/org/');
  
  // For platform admin view, ignore organization selector - show all timesheets
  // For organization view, use the organization context
  const currentOrganizationId = isPlatformAdminView ? undefined : (selectedOrganizationId || urlOrganizationId);

  // Define all data loading functions BEFORE useEffect (to avoid temporal dead zone)
  const loadTimesheetEntries = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      // For platform admin view, don't pass organizationId to show all timesheets
      // For organization view, filter by organizationId
      const params: any = {};
      if (!isPlatformAdminView && currentOrganizationId) {
        params.organizationId = currentOrganizationId;
      }
      // Add timezone parameter for timezone-aware filtering
      params.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await api.getTimesheetEntries(params);
      setTimesheetEntries(response.data || []);
    } catch (err: any) {
      console.error('Error loading timesheet entries:', err);
      setError(err.response?.data?.message || 'Failed to load timesheet entries');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await api.getUserOrganizations();
      if (response.success) {
        const transformedOrgs = response.data.map((userOrg: any) => ({
          id: userOrg.organization.id,
          name: userOrg.organization.name
        }));
        setOrganizations(transformedOrgs);
      }
    } catch (err: any) {
      console.error('Error loading organizations:', err);
    }
  };

  const loadCompanies = async (organizationId: string) => {
    if (!organizationId) return;
    
    try {
      setLoadingCompanies(true);
      const response = await api.getCompanies({ organizationId, isActive: 'true' });
      setCompanies(response.data || []);
    } catch (err: any) {
      console.error('Error loading companies:', err);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadAccounts = async (companyId: string) => {
    if (!companyId) return;
    
    try {
      setLoadingAccounts(true);
      const response = await api.getAccounts({ companyId, isActive: 'true' });
      setAccounts(response.data || []);
    } catch (err: any) {
      console.error('Error loading accounts:', err);
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadTasks = async (accountId: string) => {
    if (!accountId) return;
    
    try {
      setLoadingTasks(true);
      const response = await api.getTasks({ accountId, isActive: 'true' });
      setTasks(response.data || []);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    // Calendar fetches its own date-scoped entries; table does too.
    // Only preload the legacy list path when neither specialized view owns loading.
    if (currentView === 'calendar' || currentView === 'table') {
      loadOrganizations();
      if (currentOrganizationId && !isPlatformAdminView) {
        setFormData(prev => ({ ...prev, organizationId: currentOrganizationId }));
      }
      return;
    }
    loadTimesheetEntries();
    loadOrganizations();
    if (currentOrganizationId && !isPlatformAdminView) {
      setFormData(prev => ({ ...prev, organizationId: currentOrganizationId }));
      loadCompanies(currentOrganizationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId, isPlatformAdminView, location.pathname, currentView]);

  // Handle calendar view
  if (currentView === 'calendar') {
    if (!currentOrganizationId) {
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Please select an organization to view timesheets
          </Typography>
        </Box>
      );
    }

    const handleEntryCreate = async (entry: Partial<TimesheetEntry>) => {
      const response = await api.createTimesheetEntry(entry);
      if (!response.success) {
        throw new Error(response.message || 'Failed to create entry');
      }
    };

    const handleEntryUpdate = async (id: string, entry: Partial<TimesheetEntry>) => {
      const response = await api.updateTimesheetEntry(id, entry);
      if (!response.success) {
        throw new Error(response.message || 'Failed to update entry');
      }
    };

    const handleEntryDelete = async (id: string) => {
      const response = await api.deleteTimesheetEntry(id);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete entry');
      }
    };

    return (
      <Box
        sx={{
          width: '100%',
          flex: '1 1 0',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TimesheetCalendar
          organizationId={currentOrganizationId}
          onEntryCreate={handleEntryCreate}
          onEntryUpdate={handleEntryUpdate}
          onEntryDelete={handleEntryDelete}
        />
      </Box>
    );
  }

  // Handle table view
  if (currentView === 'table') {
    if (!currentOrganizationId) {
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Please select an organization to view timesheets
          </Typography>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          width: '100%',
          flex: '1 1 0',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        <TimesheetTableView
          organizationId={currentOrganizationId}
          onRefresh={() => loadTimesheetEntries(false)}
        />
      </Box>
    );
  }

  const handleOpenDialog = (entry?: TimesheetEntry) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        organizationId: entry.organizationId,
        companyId: entry.task?.account?.company?.id || '',
        accountId: entry.task?.account?.id || '',
        taskId: entry.taskId,
        // ❌ REMOVED: date field - date is derived from startTime
        startTime: entry.startTime ? toDateTimeLocal(entry.startTime) : '',
        stopTime: entry.stopTime ? toDateTimeLocal(entry.stopTime) : '',
        hours: entry.hours ? entry.hours.toString() : '',
        description: entry.description || '',
        approvalStatus: entry.approvalStatus
      });
      
      // Load the hierarchical data for editing
      if (entry.organizationId) {
        loadCompanies(entry.organizationId);
        if (entry.task?.account?.company?.id) {
          loadAccounts(entry.task.account.company.id);
        }
        if (entry.task?.account?.id) {
          loadTasks(entry.task.account.id);
        }
      }
    } else {
      setEditingEntry(null);
      setFormData({
        organizationId: currentOrganizationId || '',
        companyId: '',
        accountId: '',
        taskId: '',
        // ❌ REMOVED: date field
        startTime: '',
        stopTime: '',
        hours: '',
        description: '',
        approvalStatus: 'draft'
      });
      
      // Clear dependent dropdowns
      setCompanies([]);
      setAccounts([]);
      setTasks([]);
      
      // Load companies for the current organization
      if (currentOrganizationId) {
        loadCompanies(currentOrganizationId);
      }
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEntry(null);
    setFormData({
      organizationId: currentOrganizationId || '',
      companyId: '',
      accountId: '',
      taskId: '',
      // ❌ REMOVED: date field
      startTime: '',
      stopTime: '',
      hours: '',
      description: '',
      approvalStatus: 'draft'
    });
    
    // Clear dependent dropdowns
    setCompanies([]);
    setAccounts([]);
    setTasks([]);
  };

  // Handle dropdown changes with cascading updates
  const handleOrganizationChange = (organizationId: string) => {
    setFormData(prev => ({
      ...prev,
      organizationId,
      companyId: '',
      accountId: '',
      taskId: ''
    }));
    
    // Clear dependent dropdowns
    setCompanies([]);
    setAccounts([]);
    setTasks([]);
    
    // Load companies for the selected organization
    if (organizationId) {
      loadCompanies(organizationId);
    }
  };

  const handleCompanyChange = (companyId: string) => {
    setFormData(prev => ({
      ...prev,
      companyId,
      accountId: '',
      taskId: ''
    }));
    
    // Clear dependent dropdowns
    setAccounts([]);
    setTasks([]);
    
    // Load accounts for the selected company
    if (companyId) {
      loadAccounts(companyId);
    }
  };

  const handleAccountChange = (accountId: string) => {
    setFormData(prev => ({
      ...prev,
      accountId,
      taskId: ''
    }));
    
    // Clear dependent dropdown
    setTasks([]);
    
    // Load tasks for the selected account
    if (accountId) {
      loadTasks(accountId);
    }
  };

  const handleSubmit = async () => {
    try {
      console.log('🚀 [TIMESHEET DEBUG] Starting timesheet submission process');
      console.log('📋 [TIMESHEET DEBUG] Form data:', {
        formData,
        editingEntry: editingEntry?.id || 'new entry',
        currentOrganizationId,
        timestamp: new Date().toISOString()
      });

      // Validate required fields
      if (!formData.organizationId || !formData.taskId) {
        const missingFields = [];
        if (!formData.organizationId) missingFields.push('Organization');
        if (!formData.taskId) missingFields.push('Task');
        
        console.error('❌ [TIMESHEET DEBUG] Validation failed - missing fields:', missingFields);
        setError(`${missingFields.join(', ')} are required`);
        return;
      }

      // Validate either hours or startTime/stopTime
      if (!formData.hours && (!formData.startTime || !formData.stopTime)) {
        console.error('❌ [TIMESHEET DEBUG] Validation failed - either hours or startTime/stopTime required');
        setError('Either hours or both start time and stop time are required');
        return;
      }

      const submitData = {
        organizationId: formData.organizationId,
        taskId: formData.taskId,
        // ❌ REMOVED: date field
        hours: formData.hours ? parseFloat(formData.hours) : undefined,
        startTime: formData.startTime ? fromDateTimeLocal(formData.startTime) : undefined,
        stopTime: formData.stopTime ? fromDateTimeLocal(formData.stopTime) : undefined,
        description: formData.description || '',
        approvalStatus: formData.approvalStatus
      };

      console.log('📤 [TIMESHEET DEBUG] Prepared submit data:', {
        submitData,
        dataSize: JSON.stringify(submitData).length,
        hasHours: !!submitData.hours,
        hasTimeRange: !!(submitData.startTime && submitData.stopTime),
        operation: editingEntry ? 'UPDATE' : 'CREATE'
      });

      // Log API call details
      const apiMethod = editingEntry ? 'updateTimesheetEntry' : 'createTimesheetEntry';
      const apiUrl = editingEntry ? `/timesheet-entries/${editingEntry.id}` : '/timesheet-entries';
      
      console.log('🌐 [TIMESHEET DEBUG] API call details:', {
        method: editingEntry ? 'PUT' : 'POST',
        url: apiUrl,
        apiMethod,
        entryId: editingEntry?.id || 'new'
      });

      let response;
      if (editingEntry) {
        console.log('✏️ [TIMESHEET DEBUG] Updating existing entry...');
        response = await api.updateTimesheetEntry(editingEntry.id, submitData);
      } else {
        console.log('➕ [TIMESHEET DEBUG] Creating new entry...');
        response = await api.createTimesheetEntry(submitData);
      }

      console.log('✅ [TIMESHEET DEBUG] API call successful:', {
        response: response,
        success: response?.success,
        message: response?.message,
        dataId: response?.data?.id
      });

      handleCloseDialog();
      console.log('🔄 [TIMESHEET DEBUG] Reloading timesheet entries...');
      await loadTimesheetEntries();
      console.log('🎉 [TIMESHEET DEBUG] Timesheet submission completed successfully');
      
    } catch (err: any) {
      console.error('💥 [TIMESHEET DEBUG] Error occurred during submission:', {
        error: err,
        message: err.message,
        response: err.response,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: {
          method: err.config?.method,
          url: err.config?.url,
          data: err.config?.data
        },
        stack: err.stack
      });

      const errorMessage = err.response?.data?.message || err.message || 'Failed to save timesheet entry';
      console.error('🚨 [TIMESHEET DEBUG] Setting error message:', errorMessage);
      setError(errorMessage);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (window.confirm('Are you sure you want to delete this timesheet entry?')) {
      try {
        await api.deleteTimesheetEntry(entryId);
        loadTimesheetEntries();
      } catch (err: any) {
        console.error('Error deleting timesheet entry:', err);
        setError(err.response?.data?.message || 'Failed to delete timesheet entry');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'submitted':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'submitted':
        return 'Submitted';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };

  const filteredEntries = timesheetEntries.filter(entry => {
    const matchesSearch = (entry.description && entry.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (entry.task?.name && entry.task.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || entry.approvalStatus === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const columns: GridColDef[] = [
    { 
      field: 'organization', 
      headerName: 'Organization', 
      width: 180,
      valueGetter: (params) => params.row.organization?.name || 'N/A'
    },
    { 
      field: 'company', 
      headerName: 'Company', 
      width: 180,
      valueGetter: (params) => params.row.task?.account?.company?.name || 'N/A'
    },
    { 
      field: 'account', 
      headerName: 'Account', 
      width: 180,
      valueGetter: (params) => params.row.task?.account?.name || 'N/A'
    },
    { 
      field: 'task', 
      headerName: 'Task', 
      flex: 1, minWidth: 200,
      valueGetter: (params) => params.row.task?.name || 'N/A'
    },
    { 
      field: 'hours', 
      headerName: 'Hours', 
      width: 100,
      valueFormatter: (params) => params.value ? `${params.value}h` : 'N/A'
    },
    { 
      field: 'startTime', 
      headerName: 'Start Time', 
      width: 180,
      valueFormatter: (params) => params.value ? formatDateTime(params.value) : 'N/A'
    },
    { 
      field: 'stopTime', 
      headerName: 'Stop Time', 
      width: 180,
      valueFormatter: (params) => params.value ? formatDateTime(params.value) : 'N/A'
    },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { 
      field: 'approvalStatus', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={getStatusLabel(params.value)} 
          color={getStatusColor(params.value) as any}
          size="small"
        />
      )
    },
    { 
      field: 'user', 
      headerName: 'User', 
      width: 150,
      valueGetter: (params) => params.row.user?.displayName || params.row.user?.email || 'N/A'
    },
    { 
      field: 'createdAt', 
      headerName: 'Created', 
      width: 120,
      valueFormatter: (params) => formatDateOnly(params.value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteEntry(params.row.id)}
        />
      ]
    }
  ];

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Timesheets Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Entry
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search entries"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter by Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="submitted">Submitted</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                {filteredEntries.length} of {timesheetEntries.length} entries
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Timesheet Entries Table */}
      <DataTable
        rows={filteredEntries}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
      />

      {/* Add/Edit Timesheet Entry Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEntry ? 'Edit Timesheet Entry' : 'Add New Timesheet Entry'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Organization Dropdown */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={formData.organizationId}
                    label="Organization"
                    onChange={(e) => handleOrganizationChange(e.target.value)}
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Company Dropdown */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={formData.companyId}
                    label="Company"
                    onChange={(e) => handleCompanyChange(e.target.value)}
                    disabled={!formData.organizationId || loadingCompanies}
                  >
                    {loadingCompanies ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading companies...
                      </MenuItem>
                    ) : (
                      companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* Account Dropdown */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Account</InputLabel>
                  <Select
                    value={formData.accountId}
                    label="Account"
                    onChange={(e) => handleAccountChange(e.target.value)}
                    disabled={!formData.companyId || loadingAccounts}
                  >
                    {loadingAccounts ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading accounts...
                      </MenuItem>
                    ) : (
                      accounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* Task Dropdown */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Task</InputLabel>
                  <Select
                    value={formData.taskId}
                    label="Task"
                    onChange={(e) => setFormData({ ...formData, taskId: e.target.value })}
                    disabled={!formData.accountId || loadingTasks}
                  >
                    {loadingTasks ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading tasks...
                      </MenuItem>
                    ) : (
                      tasks.map((task) => (
                        <MenuItem key={task.id} value={task.id}>
                          {task.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              {/* ❌ REMOVED: Date field - date is derived from startTime */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="datetime-local"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Stop Time"
                  type="datetime-local"
                  value={formData.stopTime}
                  onChange={(e) => setFormData({ ...formData, stopTime: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hours"
                  type="number"
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                  inputProps={{ min: 0, step: 0.25 }}
                  helperText="Either enter hours manually or use start/stop times"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Approval Status</InputLabel>
                  <Select
                    value={formData.approvalStatus}
                    label="Approval Status"
                    onChange={(e) => setFormData({ ...formData, approvalStatus: e.target.value })}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="submitted">Submitted</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingEntry ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
